/**
 * detectOperationalSignals.js
 *
 * Reads the session timeline and runs all detectors.
 * Returns an array of active signals sorted by score (highest first).
 *
 * Design:
 *  - Pure recomputation — no accumulated state beyond the timeline
 *  - Each call reads the current timeline snapshot and re-evaluates
 *  - Max 20 active signals returned (FASE 18.14 performance constraint)
 */

import { getTimeline } from "../kevia-runtime/sessionTimeline";
import {
  detectFollowUpRisk,
  detectInactiveFamilyPattern,
  detectHighVisitActivity,
  detectAdminAttention,
  detectAuditInvestigation,
  detectPrayerFocus,
  detectLeadershipReview,
  detectMemberRecoveryFlow,
} from "./signalScoring";

const MAX_SIGNALS = 20;

const DETECTORS = [
  detectFollowUpRisk,
  detectInactiveFamilyPattern,
  detectHighVisitActivity,
  detectAdminAttention,
  detectAuditInvestigation,
  detectPrayerFocus,
  detectLeadershipReview,
  detectMemberRecoveryFlow,
];

/**
 * Computes all active operational signals from the current session timeline.
 *
 * @returns {Array<{ id, score, confidence, triggers, expiresAt, relatedEntities, label, description, mood }>}
 */
export function computeSignals() {
  const timeline = getTimeline();
  const now      = Date.now();

  return DETECTORS
    .map((detect) => {
      try { return detect(timeline); } catch { return null; }
    })
    .filter((s) => s !== null && s.expiresAt > now)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SIGNALS);
}
